import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Wallet,
  Banknote,
  Gift,
  Home,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  Download,
  FileText,
  FileSpreadsheet,
  Percent,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

const MONTH_NAMES_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

// Format date to Turkish format (DD.MM.YYYY)
const formatDateTR = (dateStr) => {
  if (!dateStr) return "-";
  // Handle different date formats
  if (dateStr.includes('T')) {
    const date = new Date(dateStr);
    if (!isNaN(date)) {
      return format(date, "dd.MM.yyyy", { locale: getDateLocale() });
    }
  } else if (dateStr.includes('-')) {
    // Format: YYYY-MM-DD
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
  }
  return dateStr;
};

export default function MyFinancesModule({ user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "reports", "employees"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const MONTH_NAMES = MONTH_NAMES_TR.map((m, i) => t(`auth:month${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i]}`) || m);

  const [loading, setLoading] = useState(true);
  const [trainer, setTrainer] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState("overview");
  
  // Determine if user is trainer or employee
  const isTrainer = user?.role === "trainer";
  const isEmployee = user?.role === "employee";
  const currentEntity = isTrainer ? trainer : employee;
  
  console.log("User role:", user?.role, "isTrainer:", isTrainer, "isEmployee:", isEmployee, "currentEntity:", currentEntity);
  
  // Financial data
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [pendingSalaries, setPendingSalaries] = useState([]); // Bekleyen maaşlar
  const [pendingRents, setPendingRents] = useState([]); // Bekleyen kiralar
  const [pendingCommissionDebts, setPendingCommissionDebts] = useState([]); // Bekleyen prim borçları (detaylı)
  const [commissionPayments, setCommissionPayments] = useState([]); // Hesaplanan primler (detaylar)
  const [commissionPaidRecords, setCommissionPaidRecords] = useState([]); // Ödenen prim kayıtları
  const [bonusPayments, setBonusPayments] = useState([]);
  const [debts, setDebts] = useState([]);
  const [debtPayments, setDebtPayments] = useState([]); // Paid rent/commission TO business
  const [summary, setSummary] = useState({
    totalIncome: 0,
    totalExpense: 0,
    totalDebt: 0,
    pendingSalaryTotal: 0, // Bekleyen maaş toplamı
    netBalance: 0,
    salaryTotal: 0,
    commissionTotal: 0, // Hesaplanan toplam prim
    commissionPaidTotal: 0, // Ödenen toplam prim
    commissionPendingTotal: 0, // Bekleyen toplam prim
    externalCommission: 0,  // Commission paid to business for external members
    netCommission: 0,  // Net commission after external member payments
    bonusTotal: 0,
    rentDebt: 0,
    commissionDebt: 0,
    rentPaid: 0,
    commissionPaid: 0
  });

  useEffect(() => {
    if (user?.user_id) {
      if (isTrainer) {
        fetchTrainerData();
      } else if (isEmployee) {
        fetchEmployeeData();
      }
    }
  }, [user]);

  useEffect(() => {
    if (currentEntity) {
      fetchFinancialData();
    }
  }, [trainer, employee, selectedMonth, selectedYear]);

  const fetchTrainerData = async () => {
    try {
      const response = await fetch(`${API}/trainers/by-user/${user.user_id}`, {
        credentials: "include"
      });
      if (response.ok) {
        const trainers = await response.json();
        if (trainers.length > 0) {
          setTrainer(trainers[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching trainer:", error);
    }
  };

  const fetchEmployeeData = async () => {
    try {
      const response = await fetch(`${API}/employees/by-user/${user.user_id}`, {
        credentials: "include"
      });
      if (response.ok) {
        const employees = await response.json();
        if (employees.length > 0) {
          setEmployee(employees[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialData = async () => {
    if (!currentEntity) {
      console.log("No current entity found - trainer:", trainer, "employee:", employee);
      return;
    }
    setLoading(true);
    
    const businessId = isTrainer ? trainer?.business_id : employee?.business_id;
    const entityId = isTrainer ? trainer?.trainer_id : employee?.employee_id;
    const entityType = isTrainer ? "trainers" : "employees";
    
    console.log("Fetching financial data for:", entityType, entityId);
    
    try {
      // Fetch all financial data in parallel
      const fetchPromises = [
        // Accruals based finance - use entity-specific endpoint
        fetch(`${API}/${entityType}/${entityId}/my-debts`, {
          credentials: "include"
        }).catch((err) => { console.error("my-debts error:", err); return { ok: false }; }),
        // Bonus payments
        fetch(`${API}/${entityType}/${entityId}/bonus-payments`, {
          credentials: "include"
        }).catch(() => ({ ok: false })),
      ];
      
      // Trainer-specific: commissions
      if (isTrainer) {
        fetchPromises.push(
          fetch(`${API}/trainers/${entityId}/my-commissions?month=${selectedMonth}&year=${selectedYear}`, {
            credentials: "include"
          }).catch(() => ({ ok: false })),
        );
      } else {
        // Employee-specific: commissions
        fetchPromises.push(
          fetch(`${API}/employees/${entityId}/my-commissions?month=${selectedMonth}&year=${selectedYear}`, {
            credentials: "include"
          }).catch(() => ({ ok: false }))
        );
      }

      const responses = await Promise.all(fetchPromises);
      
      let salaryData = [];
      let rentDebt = 0;
      let commissionDebt = 0;
      let rentPaid = 0;
      let commissionPaid = 0;
      let salaryTotal = 0;
      let pendingSalaryTotal = 0;
      
      // Process accruals finance data (from /my-debts endpoint)
      if (responses[0].ok) {
        const financeData = await responses[0].json();
        console.log("Financial data received:", financeData);
        
        // Filter by selected month/year for paid items, but show ALL pending
        const monthKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
        
        // Salary data (money owed TO user from business)
        const allSalaryDebts = financeData.salary?.debts || [];
        // Show ALL pending salaries regardless of month
        const pendingSalariesList = allSalaryDebts.filter(d => d.status === 'pending' || d.status === 'partial');
        // Show paid salaries only for selected month
        const paidSalaries = allSalaryDebts.filter(d => d.month_key === monthKey && d.status === 'paid');
        const salaryPaymentsAll = financeData.salary?.payments || [];
        const salaryPaymentsThisMonth = salaryPaymentsAll.filter(p => {
          const date = new Date(p.payment_date || p.created_at);
          return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
        });
        
        salaryData = [...pendingSalariesList, ...paidSalaries];
        setSalaryPayments(salaryPaymentsThisMonth);
        setPendingSalaries(pendingSalariesList); // Bekleyen maaşları kaydet
        salaryTotal = salaryPaymentsThisMonth.reduce((sum, p) => sum + (p.amount || 0), 0);
        pendingSalaryTotal = pendingSalariesList.reduce((sum, d) => sum + (d.remaining_amount || 0), 0);
        
        // Rent data (money user owes TO business) - for trainers
        if (isTrainer) {
          const allRentDebts = financeData.rent?.debts || [];
          // Show ALL pending rents regardless of month
          const pendingRentList = allRentDebts.filter(d => d.status === 'pending' || d.status === 'partial');
          // Show paid rents only for selected month
          const paidRent = allRentDebts.filter(d => d.month_key === monthKey && d.status === 'paid');
          const rentPaymentsAll = financeData.rent?.payments || [];
          const rentPaymentsThisMonth = rentPaymentsAll.filter(p => {
            const date = new Date(p.payment_date || p.created_at);
            return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
          });
          
          setDebts([...pendingRentList, ...paidRent]);
          setPendingRents(pendingRentList); // Bekleyen kiraları kaydet
          rentDebt = pendingRentList.reduce((sum, d) => sum + (d.remaining_amount || 0), 0);
          
          // Commission debt data (for commission_giving trainers - prim veren)
          const commissionDebtData = financeData.commission_debt || {};
          const allCommissionDebts = commissionDebtData.debts || [];
          const pendingCommissionDebtsList = allCommissionDebts.filter(d => d.status === 'pending' || d.status === 'partial');
          setPendingCommissionDebts(pendingCommissionDebtsList); // Save detailed commission debts
          commissionDebt = pendingCommissionDebtsList.reduce((sum, d) => sum + ((d.amount || 0) - (d.paid_amount || 0)), 0);
          
          // Commission payments made
          const commissionDebtPayments = commissionDebtData.payments || [];
          const commissionDebtPaymentsThisMonth = commissionDebtPayments.filter(p => {
            const date = new Date(p.payment_date || p.created_at);
            return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
          });
          commissionPaid = commissionDebtPaymentsThisMonth.reduce((sum, p) => sum + (p.amount || 0), 0);
          
          // Combine rent and commission payments for debt payments display
          const allPayments = [
            ...rentPaymentsThisMonth.map(p => ({ ...p, type: 'rent' })),
            ...commissionDebtPaymentsThisMonth.map(p => ({ ...p, type: 'commission' }))
          ];
          setDebtPayments(allPayments);
          rentPaid = rentPaymentsThisMonth.reduce((sum, p) => sum + (p.amount || 0), 0);
        }
      }

      // Process bonus payments
      let bonusTotal = 0;
      if (responses[1].ok) {
        const bonusData = await responses[1].json();
        const monthBonuses = bonusData.filter(b => {
          const date = new Date(b.payment_date || b.created_at);
          return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
        });
        setBonusPayments(monthBonuses);
        bonusTotal = monthBonuses.reduce((sum, b) => sum + (b.amount || 0), 0);
      }

      let commissionTotal = 0;
      let externalCommission = 0;
      let netCommission = 0;
      let commissionPaidTotal = 0;
      let commissionPendingTotal = 0;

      // Process commission data
      if (responses[2] && responses[2].ok) {
        const data = await responses[2].json();
        commissionTotal = data.total_commission || 0;
        externalCommission = data.external_commission || 0;
        netCommission = data.net_commission || commissionTotal;
        commissionPaidTotal = data.paid_amount || 0;
        commissionPendingTotal = data.pending_amount || (commissionTotal - commissionPaidTotal);
        // Set commission details for both trainers and employees
        setCommissionPayments(data.details || data.commissions || []);
        // Set commission payment records (actual payments made)
        setCommissionPaidRecords(data.commission_payments || []);
      }
      
      // Check trainer work type
      const isCommissionGiving = isTrainer && trainer?.work_type === 'commission_giving';
      const isRentGiving = isTrainer && trainer?.work_type === 'rent_giving';
      const isDebtTypeTrainer = isCommissionGiving || isRentGiving;
      
      // Calculate totals based on trainer type
      let totalIncome = 0;
      let totalExpense = 0;
      let totalDebt = 0;
      
      // GELİR: TÜM ANTRENÖRLER İÇİN
      // Alınan Maaş + Bekleyen Maaş + Alınan Prim + Bekleyen Prim + Bonus
      // Toplam gelir = tüm kazanılan ve kazanılacak tutarlar
      totalIncome = salaryTotal + pendingSalaryTotal + commissionPaidTotal + commissionPendingTotal + bonusTotal;
      
      // GİDER: Antrenör tipine göre değişir
      if (isCommissionGiving) {
        // PRİM VEREN ANTRENÖR:
        // - Gider: Ödenen prim borcu
        // - Borç: Bekleyen prim borcu
        totalExpense = commissionPaid;
        totalDebt = commissionDebt;
      } else if (isRentGiving) {
        // KİRA VEREN ANTRENÖR:
        // - Gider: Ödenen kira
        // - Borç: Bekleyen kira borcu
        totalExpense = rentPaid;
        totalDebt = rentDebt;
      } else {
        // PRİM ALAN / MAAŞ ALAN ANTRENÖR:
        // - Gider: Yok (borç vermiyor)
        // - Borç: Yok
        totalExpense = 0;
        totalDebt = 0;
      }
      
      // Net balance = income - expenses - debts
      const netBalance = totalIncome - totalExpense - totalDebt;
      
      setSummary({
        totalIncome,
        totalExpense,
        totalDebt,
        pendingSalaryTotal,
        netBalance,
        salaryTotal,
        commissionTotal,
        commissionPaidTotal,
        commissionPendingTotal,
        externalCommission,
        netCommission,
        bonusTotal,
        rentDebt,
        commissionDebt,
        rentPaid,
        commissionPaid
      });

    } catch (error) {
      console.error("Error fetching financial data:", error);
      toast.error(t("reports:toast_finansalVerilerYuklenirkenHataOlustu"));
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleExportFinances = async (format) => {
    if (!currentEntity) return;
    
    try {
      toast.info(t("trainers:reportPreparing", { format: format.toUpperCase() }));
      
      const entityId = isTrainer ? trainer.trainer_id : employee.employee_id;
      const entityType = isTrainer ? "trainers" : "employees";
      
      const response = await fetch(
        `${API}/${entityType}/${entityId}/my-finances/export?format=${format}&month=${selectedMonth}&year=${selectedYear}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Decode base64 and create blob
        const byteCharacters = atob(data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.mime_type });
        
        // Download file
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        
        toast.success(t('common:reportDownloaded'));
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Rapor indirilemedi");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error(t("reports:toast_raporIndirmeHatasi"));
    }
  };

  const getWorkTypeLabel = (type) => {
    const labels = {
      commission_receiving: t("reports:commissionReceiving"),
      salary_receiving: t("employees:salaryReceiving"),
      salary_and_commission_receiving: t("reports:salaryAndCommissionReceiving"),
      commission_giving: t("reports:commissionGiving"),
      rent_giving: t("reports:rentGiving")
    };
    return labels[type] || type;
  };

  const getWorkTypeColor = (type) => {
    if (['commission_receiving', 'salary_receiving', 'salary_and_commission_receiving'].includes(type)) {
      return "bg-green-100 text-green-700";
    }
    return "bg-orange-100 text-orange-700";
  };

  if (loading && !currentEntity) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  if (!currentEntity) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 text-grey mx-auto mb-4" />
        <p className="text-grey">{t("trainers:entityInfoNotFound", { role: isTrainer ? t("trainers:trainerRole") : t("trainers:employee") })}</p>
      </div>
    );
  }

  const isDebtType = isTrainer && ['commission_giving', 'rent_giving'].includes(trainer?.work_type);
  const isIncomeType = isTrainer && ['commission_receiving', 'salary_receiving', 'salary_and_commission_receiving'].includes(trainer?.work_type);
  
  // Alternative check: if there are commission debts, show them regardless of work_type
  const hasCommissionDebts = summary.commissionDebt > 0 || summary.commissionPaid > 0 || pendingCommissionDebts.length > 0;
  const hasRentDebts = summary.rentDebt > 0 || summary.rentPaid > 0 || pendingRents.length > 0;

  // Use totals from summary (already calculated in fetchFinancialData)
  const totalIncome = summary.totalIncome;
  const totalExpense = summary.totalExpense;
  const totalDebt = summary.totalDebt;
  const netBalance = summary.netBalance;

  return (
    <div className="space-y-6">
      {/* Header - Same as Reports */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t("reports:finanslarim")}</h2>
          <div className="flex items-center gap-2 mt-1">
            {isTrainer && (
              <Badge className={getWorkTypeColor(trainer.work_type)}>
                {getWorkTypeLabel(trainer.work_type)}
              </Badge>
            )}
            {isEmployee && (
              <Badge className="bg-blue-100 text-blue-700">{t("trainers:employee")}</Badge>
            )}
          </div>
        </div>

        {/* Date Range - Same as Reports */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-white rounded-xl border p-2">
            <Button variant="ghost" size="sm" onClick={handlePreviousMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-[140px] justify-center">
              <Calendar className="w-4 h-4 text-navy" />
              <span className="font-medium text-navy">
                {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleNextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Export Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t("reports:report")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExportFinances('pdf')}>
                <FileText className="w-4 h-4 mr-2 text-red-500" />
                {t("trainers:downloadPdf")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportFinances('excel')}>
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                {t("trainers:downloadExcel")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Cards - Same style as Reports */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {/* Maaş */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-4">
            <p className="text-xs text-green-600 mb-1">{t('trainers:salary')}</p>
            <p className="text-lg font-bold text-green-700">{currencySymbol}{summary.salaryTotal.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
        
        {/* Prim (Alınan) */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <p className="text-xs text-blue-600 mb-1">{t("reports:receivedCommission")}</p>
            <p className="text-lg font-bold text-blue-700">{currencySymbol}{summary.commissionTotal.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
        
        {/* Bonus */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-violet-50">
          <CardContent className="p-4">
            <p className="text-xs text-purple-600 mb-1">Bonus</p>
            <p className="text-lg font-bold text-purple-700">{currencySymbol}{summary.bonusTotal.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
        
        {/* Kira Borcu */}
        {isTrainer && trainer.work_type === 'rent_giving' && (
          <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-amber-50">
            <CardContent className="p-4">
              <p className="text-xs text-orange-600 mb-1">{t("trainers:rentDebts")}</p>
              <p className="text-lg font-bold text-orange-700">-{currencySymbol}{summary.rentDebt.toLocaleString('tr-TR')}</p>
            </CardContent>
          </Card>
        )}
        
        {/* Prim Borcu */}
        {isTrainer && trainer.work_type === 'commission_giving' && (
          <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-rose-50">
            <CardContent className="p-4">
              <p className="text-xs text-red-600 mb-1">{t("trainers:commissionDebt")}</p>
              <p className="text-lg font-bold text-red-700">-{currencySymbol}{summary.commissionDebt.toLocaleString('tr-TR')}</p>
            </CardContent>
          </Card>
        )}
        
        {/* Net */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-gray-50">
          <CardContent className="p-4">
            <p className="text-xs text-slate-600 mb-1">{t("trainers:netBalance")}</p>
            <p className={`text-lg font-bold ${netBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {netBalance >= 0 ? '+' : ''}{currencySymbol}{netBalance.toLocaleString('tr-TR')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Two Column Layout like Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GELİRLER Card */}
        <Card className="border-0 shadow-sm border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-green-700 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              {t("trainers:income")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Maaş Hakedişleri - Alınan */}
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Banknote className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">{t("employees:receivedSalary")}</p>
                  <p className="text-xs text-green-600">{t("employees:salaryFromBusiness")}</p>
                </div>
              </div>
              <p className="font-bold text-green-700">+{currencySymbol}{summary.salaryTotal.toLocaleString('tr-TR')}</p>
            </div>
            
            {/* Maaş Hakedişleri - Bekleyen */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">{t("employees:pendingSalary")}</p>
                  <p className="text-xs text-amber-600">{t("employees:henuzOdenmemisMaas")}</p>
                </div>
              </div>
              <p className="font-bold text-amber-700">+{currencySymbol}{summary.pendingSalaryTotal.toLocaleString('tr-TR')}</p>
            </div>
            
            {/* Alınan Primler */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Percent className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">{t("reports:receivedCommission")}</p>
                  <p className="text-xs text-blue-600">{t("reports:odenenDersPrimleri")}</p>
                </div>
              </div>
              <p className="font-bold text-blue-700">+{currencySymbol}{summary.commissionPaidTotal.toLocaleString('tr-TR')}</p>
            </div>
            
            {/* Bekleyen Primler */}
            <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-cyan-600" />
                <div>
                  <p className="font-medium text-cyan-800">{t("trainers:pendingCommission")}</p>
                  <p className="text-xs text-cyan-600">{t("reports:henuzOdenmemisPrimler")}</p>
                </div>
              </div>
              <p className="font-bold text-cyan-700">+{currencySymbol}{summary.commissionPendingTotal.toLocaleString('tr-TR')}</p>
            </div>
            
            {/* Bonus Ödemeler */}
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Gift className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-purple-800">{t("common:bonusOdemeler")}</p>
                  <p className="text-xs text-purple-600">{t("admin:ekOdemeler")}</p>
                </div>
              </div>
              <p className="font-bold text-purple-700">+{currencySymbol}{summary.bonusTotal.toLocaleString('tr-TR')}</p>
            </div>
            
            {/* Toplam Gelir */}
            <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg border border-green-200 mt-4">
              <p className="font-bold text-green-800">{t("common:totalRevenue")}</p>
              <p className="font-bold text-xl text-green-700">+{currencySymbol}{totalIncome.toLocaleString('tr-TR')}</p>
            </div>
          </CardContent>
        </Card>

        {/* GİDERLER Card */}
        <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-red-700 flex items-center gap-2">
              <TrendingDown className="w-5 h-5" />
              {t("trainers:expenses")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Kira Ödemeleri - rent_giving antrenör için VEYA kira borcu varsa */}
            {(isDebtType && trainer?.work_type === 'rent_giving') || hasRentDebts ? (
              <>
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Home className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-orange-800">{t("common:odenenKira")}</p>
                      <p className="text-xs text-orange-600">{t("common:rentPaidToBusiness")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-orange-700">-{currencySymbol}{summary.rentPaid.toLocaleString('tr-TR')}</p>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium text-red-800">{t("trainers:pendingRentDebt")}</p>
                      <p className="text-xs text-red-600">{t("common:unpaidDebt")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-red-700">-{currencySymbol}{summary.rentDebt.toLocaleString('tr-TR')}</p>
                </div>
              </>
            ) : null}
            
            {/* Prim Ödemeleri - commission_giving antrenör için VEYA prim borcu varsa */}
            {(isDebtType && trainer?.work_type === 'commission_giving') || hasCommissionDebts ? (
              <>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Percent className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-800">{t("reports:odenenPrim")}</p>
                      <p className="text-xs text-purple-600">{t("reports:commPaidToBusiness")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-purple-700">-{currencySymbol}{summary.commissionPaid.toLocaleString('tr-TR')}</p>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium text-red-800">{t("trainers:pendingCommissionDebt")}</p>
                      <p className="text-xs text-red-600">{t("common:unpaidDebt")}</p>
                    </div>
                  </div>
                  <p className="font-bold text-red-700">-{currencySymbol}{summary.commissionDebt.toLocaleString('tr-TR')}</p>
                </div>
              </>
            ) : null}
            
            {/* Maaş alan antrenör için gider yok - sadece hiç borç yoksa göster */}
            {!hasCommissionDebts && !hasRentDebts && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-600">Gider Yok</p>
                    <p className="text-xs text-slate-500">
                      {isIncomeType ? t("employees:noDebtForSalary") : "Bu dönemde gider veya borç yok"}
                    </p>
                  </div>
                </div>
                <p className="font-bold text-slate-400">{currencySymbol}0</p>
              </div>
            )}
            
            {/* Toplam Gider */}
            <div className="flex items-center justify-between p-3 bg-red-100 rounded-lg border border-red-200 mt-4">
              <p className="font-bold text-red-800">{t("trainers:totalExpenseAndDebt")}</p>
              <p className="font-bold text-xl text-red-700">
                -{currencySymbol}{(totalExpense + totalDebt).toLocaleString('tr-TR')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* NET BAKİYE Card */}
      <Card className="border-0 shadow-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${netBalance >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                {netBalance >= 0 
                  ? <TrendingUp className="w-6 h-6 text-green-600" />
                  : <TrendingDown className="w-6 h-6 text-red-600" />
                }
              </div>
              <div>
                <p className="text-grey text-sm">{t("common:netBaki̇ye")}</p>
                <p className="text-xs text-grey">{MONTH_NAMES[selectedMonth - 1]} {selectedYear}</p>
              </div>
            </div>
            <p className={`font-bold text-3xl ${netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {netBalance >= 0 ? '+' : ''}{currencySymbol}{netBalance.toLocaleString('tr-TR')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
        <TabsList className="bg-slate-100 rounded-xl p-1 flex-wrap">
          <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white">
            📊 {t("trainers:summary")}
          </TabsTrigger>
          <TabsTrigger value="income" className="rounded-lg data-[state=active]:bg-white text-green-600">
            {t("trainers:incomeTab")}
          </TabsTrigger>
          <TabsTrigger value="expenses" className="rounded-lg data-[state=active]:bg-white text-red-600">
            {t("trainers:expensesTab")}
          </TabsTrigger>
        </TabsList>

        {/* Summary Tab - Shows all key metrics */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          {/* Income Summary Card */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-green-700 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                {t("trainers:incomeSummary")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-green-800">{t("employees:receivedSalary")}</span>
                <span className="font-bold text-green-700">+{currencySymbol}{summary.salaryTotal.toLocaleString('tr-TR')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                <span className="text-amber-800">{t("employees:pendingSalary")}</span>
                <span className="font-bold text-amber-700">+{currencySymbol}{summary.pendingSalaryTotal.toLocaleString('tr-TR')}</span>
              </div>
              {/* Alınan Prim - TÜM antrenörler için göster */}
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-800">{t("reports:receivedCommission")}</span>
                <span className="font-bold text-blue-700">+{currencySymbol}{summary.commissionPaidTotal.toLocaleString('tr-TR')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-cyan-50 rounded-lg">
                <span className="text-cyan-800">{t("trainers:pendingCommission")}</span>
                <span className="font-bold text-cyan-700">+{currencySymbol}{summary.commissionPendingTotal.toLocaleString('tr-TR')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                <span className="text-purple-800">{t("common:bonusOdemeler")}</span>
                <span className="font-bold text-purple-700">+{currencySymbol}{summary.bonusTotal.toLocaleString('tr-TR')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg border-2 border-green-200">
                <span className="font-medium text-green-800">{t("common:totalRevenue")}</span>
                <span className="font-bold text-xl text-green-800">+{currencySymbol}{totalIncome.toLocaleString('tr-TR')}</span>
              </div>
            </CardContent>
          </Card>
          
          {/* Expense Summary Card */}
          {(hasCommissionDebts || hasRentDebts) && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-red-700 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5" />
                  {t("trainers:expenseSummary")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {hasRentDebts && (
                  <>
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                      <span className="text-orange-800">{t("common:odenenKira")}</span>
                      <span className="font-bold text-orange-700">-{currencySymbol}{summary.rentPaid.toLocaleString('tr-TR')}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                      <span className="text-red-800">{t("trainers:pendingRentDebt")}</span>
                      <span className="font-bold text-red-700">-{currencySymbol}{summary.rentDebt.toLocaleString('tr-TR')}</span>
                    </div>
                  </>
                )}
                {hasCommissionDebts && (
                  <>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="text-purple-800">{t("reports:odenenPrim")}</span>
                      <span className="font-bold text-purple-700">-{currencySymbol}{summary.commissionPaid.toLocaleString('tr-TR')}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                      <span className="text-red-800">{t("trainers:pendingCommissionDebt")}</span>
                      <span className="font-bold text-red-700">-{currencySymbol}{summary.commissionDebt.toLocaleString('tr-TR')}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center p-3 bg-red-100 rounded-lg border-2 border-red-200">
                  <span className="font-medium text-red-800">{t("trainers:totalExpenseAndDebt")}</span>
                  <span className="font-bold text-xl text-red-800">-{currencySymbol}{(totalExpense + totalDebt).toLocaleString('tr-TR')}</span>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Net Balance Summary */}
          <Card className="border-0 shadow-card bg-gradient-to-br from-navy to-blue-900">
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-blue-200 text-sm">{t("trainers:netBalance")}</p>
                  <p className="text-xs text-blue-300 mt-1">{t("trainers:income")} - {t("trainers:expenses")}</p>
                </div>
                <p className={`text-3xl font-bold ${netBalance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {netBalance >= 0 ? '+' : ''}{currencySymbol}{netBalance.toLocaleString('tr-TR')}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Income Tab - Detailed income breakdown */}
        <TabsContent value="income" className="mt-4 space-y-4">
          {/* Salary Section */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                <Banknote className="w-5 h-5 text-green-600" />
                {t("trainers:salaryDetails")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pending Salaries */}
              <div>
                <h4 className="font-medium text-amber-700 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {t("trainers:pendingSalaries")}
                </h4>
                {pendingSalaries.length > 0 ? (
                  <div className="space-y-2">
                    {pendingSalaries.map((salary, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium text-amber-800">{MONTH_NAMES[(salary.month || 1) - 1]} {salary.year}</p>
                          <Badge className="text-xs bg-amber-100 text-amber-700">
                            {salary.status === 'partial' ? t("trainers:partiallyPaid") : t("trainers:pendingStatus")}
                          </Badge>
                        </div>
                        <p className="font-bold text-amber-700">+{currencySymbol}{(salary.remaining_amount || salary.amount || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-grey text-sm p-3 bg-slate-50 rounded-lg">{t("employees:bekleyenMaasYok")}</p>
                )}
              </div>
              
              {/* Paid Salaries */}
              <div>
                <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {t("trainers:receivedSalariesTitle")}
                </h4>
                {salaryPayments.length > 0 ? (
                  <div className="space-y-2">
                    {salaryPayments.map((payment, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium text-green-800">{payment.payment_date || t("trainers:paymentLabel")}</p>
                          <p className="text-xs text-green-600">{t('payments:paid')}</p>
                        </div>
                        <p className="font-bold text-green-700">+{currencySymbol}{(payment.amount || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-grey text-sm p-3 bg-slate-50 rounded-lg">{t("employees:buDonemdeAlinanMaasYok")}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Commission Section (for income type trainers - prim ALAN) */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                <Percent className="w-5 h-5 text-blue-600" />
                {t("trainers:commissionDetailsReceived")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pending Commissions */}
              <div>
                <h4 className="font-medium text-amber-700 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {t("trainers:pendingCommissions")}
                </h4>
                {commissionPayments.filter(c => c.status === 'pending' || !c.payment_date).length > 0 ? (
                  <div className="space-y-2">
                    {commissionPayments.filter(c => c.status === 'pending' || !c.payment_date).map((comm, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium text-amber-800">{comm.member_name || 'Ders'}</p>
                          <p className="text-xs text-amber-600">{comm.date} {comm.start_time}</p>
                        </div>
                        <p className="font-bold text-amber-700">+{currencySymbol}{(comm.commission || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-grey text-sm p-3 bg-slate-50 rounded-lg">{t("common:noPendingCommission")}</p>
                )}
              </div>
              
              {/* Paid Commissions */}
              <div>
                <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {t("trainers:receivedCommissions")}
                </h4>
                {commissionPayments.filter(c => c.status === 'paid' || c.payment_date).length > 0 ? (
                  <div className="space-y-2">
                    {commissionPayments.filter(c => c.status === 'paid' || c.payment_date).map((comm, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium text-green-800">{comm.member_name || t("common:lessonsSingular")}</p>
                          <p className="text-xs text-green-600">{comm.date} {comm.start_time}</p>
                        </div>
                        <p className="font-bold text-green-700">+{currencySymbol}{(comm.commission || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    ))}
                  </div>
                ) : commissionPayments.length === 0 ? (
                  <p className="text-grey text-sm p-3 bg-slate-50 rounded-lg">{t("reports:buDonemdePrimKaydiYok")}</p>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* Bonus Section */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-600" />
                {t("trainers:bonusAndExtra")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bonusPayments.length > 0 ? (
                <div className="space-y-2">
                  {bonusPayments.map((bonus, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <div>
                        <p className="font-medium text-purple-800">{bonus.reason || "Bonus"}</p>
                        <p className="text-xs text-purple-600">{bonus.payment_date}</p>
                      </div>
                      <p className="font-bold text-purple-700">+{currencySymbol}{(bonus.amount || 0).toLocaleString('tr-TR')}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-grey text-center py-4">{t("common:buDonemdeBonusKaydiYok")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab - Detailed expense breakdown */}
        <TabsContent value="expenses" className="mt-4 space-y-4">
          {/* For rent_giving trainers */}
          {isTrainer && trainer.work_type === 'rent_giving' && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                  <Home className="w-5 h-5 text-orange-600" />
                  {t("trainers:rentDebts")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingRents.length > 0 ? (
                  <div className="space-y-2">
                    {pendingRents.map((rent, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                        <div>
                          <p className="font-medium text-orange-800">{MONTH_NAMES[(rent.month || 1) - 1]} {rent.year}</p>
                          <Badge className={`text-xs ${rent.status === 'paid' ? 'bg-green-100 text-green-700' : rent.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                            {rent.status === 'paid' ? t("trainers:paidStatus") : rent.status === 'partial' ? t("trainers:partialStatus") : t("trainers:pendingStatus")}
                          </Badge>
                        </div>
                        <p className="font-bold text-orange-700">-{currencySymbol}{(rent.remaining_amount || rent.amount || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-grey text-center py-4">{t("common:noPendingRentDebt")}</p>
                )}
                
                {/* Payment History */}
                {debtPayments.filter(p => (p.type || p.debt_type) === 'rent').length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium text-green-700 mb-2">{t("common:paymentsMade")}</h4>
                    {debtPayments.filter(p => (p.type || p.debt_type) === 'rent').map((payment, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-green-50 rounded-lg mb-1">
                        <span className="text-sm text-green-700">{formatDateTR(payment.payment_date)}</span>
                        <span className="font-bold text-green-600">-{currencySymbol}{(payment.amount || 0).toLocaleString('tr-TR')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* For commission_giving trainers */}
          {isTrainer && trainer.work_type === 'commission_giving' && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
                  <Percent className="w-5 h-5 text-purple-600" />
                  {t("trainers:commissionDebtsTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Show pending commission debts summary */}
                <div className="p-4 bg-purple-50 rounded-xl mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-purple-800">{t("trainers:pendingCommissionDebt")}</p>
                      <p className="text-xs text-purple-600">{t("common:disUyeDerslerindenIsletmeyeOdenmesi")}</p>
                    </div>
                    <p className="font-bold text-xl text-red-600">-{currencySymbol}{summary.commissionDebt.toLocaleString('tr-TR')}</p>
                  </div>
                </div>
                
                {/* Detailed pending commission debts list */}
                {pendingCommissionDebts.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-purple-700 mb-2">{t("common:borcDetaylari")}</h4>
                    <div className="space-y-2">
                      {pendingCommissionDebts.map((debt, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-red-800">
                              {debt.member_name || t("trainers:externalMember")}
                            </p>
                            <p className="text-xs text-red-600">
                              {formatDateTR(debt.lesson_date || debt.description?.split(' - ')[1]?.split(' ')[0])} {debt.lesson_time || ''}
                            </p>
                            {debt.status === 'partial' && (
                              <Badge className="text-xs bg-amber-100 text-amber-700 mt-1">
                                {t("trainers:partiallyPaidAmount", { amount: `${currencySymbol}${(debt.paid_amount || 0).toLocaleString('tr-TR')}` })}
                              </Badge>
                            )}
                          </div>
                          <p className="font-bold text-red-700">
                            -{currencySymbol}{((debt.amount || 0) - (debt.paid_amount || 0)).toLocaleString('tr-TR')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Show paid commissions */}
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-green-800">{t("reports:buAyOdenenPrim")}</p>
                      <p className="text-xs text-green-600">{t("reports:commsPaidToBusiness")}</p>
                    </div>
                    <p className="font-bold text-xl text-green-600">-{currencySymbol}{summary.commissionPaid.toLocaleString('tr-TR')}</p>
                  </div>
                </div>

                {/* Payment History */}
                {debtPayments.filter(p => (p.type || p.debt_type) === 'commission').length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium text-green-700 mb-2">{t("common:paymentsMade")}</h4>
                    {debtPayments.filter(p => (p.type || p.debt_type) === 'commission').map((payment, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-green-50 rounded-lg mb-1">
                        <span className="text-sm text-green-700">{formatDateTR(payment.payment_date)}</span>
                        <span className="font-bold text-green-600">-{currencySymbol}{(payment.amount || 0).toLocaleString('tr-TR')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* For income type trainers - no expenses */}
          {isIncomeType && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-green-700">{t("trainers:noExpense")}</p>
                <p className="text-grey">{t("trainers:noSalaryDebtsForIncomeTrainer")}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

